import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Tv, Copy, Check, ShieldCheck, Lightbulb } from "lucide-react";

/**
 * Establishment dashboard block displaying a permanent 4-digit TV CODE that
 * can be typed into any TV browser (Smart TV, Android TV, Fire TV, etc.) on
 * the page tv.bypassgo.app to open this establishment's public display.
 *
 * Replaces the previous "Cast on TV" / Presentation API approach which was
 * unreliable across TV browsers.
 */
export default function TVCodeBlock({ commerceId }) {
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Public TV-entry URL : on utilise le chemin /tv du domaine principal,
  // ce qui évite d'imposer un sous-domaine (et donc un certificat SSL
  // supplémentaire chez Emergent). Le frontend détecte aussi le sous-
  // domaine `tv.bypassgo.fr` et redirige vers /tv si jamais l'utilisateur
  // l'utilise — les deux URL restent donc valides.
  const tvHost = "bypassgo.fr/tv";
  const tvHref = "https://bypassgo.fr/tv";

  // Public QR code that opens /display/{commerceId} directly — handy for
  // the establishment to scan with a phone or TV remote app when no
  // keyboard is convenient.
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
  const displayQrUrl = commerceId ? `${apiBase}/commerces/${commerceId}/display-qr.png` : null;

  useEffect(() => {
    let mounted = true;
    const fetchCode = async () => {
      if (!commerceId) return;
      setLoading(true);
      try {
        const { data } = await api.get("/merchant/me/tv-code");
        if (mounted) setCode(data?.code || null);
      } catch (e) {
        if (mounted) toast.error(e?.response?.data?.detail || "Impossible de récupérer le code TV");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchCode();
    return () => { mounted = false; };
  }, [commerceId]);

  if (!commerceId) return null;

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code copié");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Code copié"); } catch { toast.error("Copie impossible"); }
      ta.remove();
    }
  };

  return (
    <div className="mb-6 border-2 border-border bg-white" data-testid="tv-code-block">
      <div className="px-4 py-2 border-b border-border bg-surface/60 flex items-center gap-2">
        <Tv size={14} />
        <p className="overline">Écran public — Code TV</p>
      </div>

      <div className="p-5">
        <p className="text-sm font-bold text-ink mb-1">Code TV de votre écran public</p>
        <p className="text-xs text-subtle mb-4">Utilisez ce code pour afficher votre écran public sur une télévision.</p>

        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          {/* The big code */}
          <div className="flex-1 min-w-0 border-2 border-border bg-surface/30 p-6 flex items-center justify-center">
            <p
              className="font-display font-black tracking-[0.3em] text-[#1B5BFF] select-all leading-none text-5xl sm:text-6xl md:text-7xl"
              data-testid="tv-code-value"
            >
              {loading ? "····" : (code || "----")}
            </p>
          </div>
          {/* Copy CTA + QR shortcut */}
          <div className="flex flex-col gap-2 shrink-0 md:w-56">
            <button
              onClick={copyCode}
              disabled={!code || loading}
              className="inline-flex items-center justify-center gap-2 h-14 px-6 bg-[#EAF1FF] text-[#1B5BFF] border-2 border-[#1B5BFF] font-display font-black text-lg disabled:opacity-50 hover:bg-[#1B5BFF] hover:text-white"
              data-testid="tv-code-copy-btn"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />} {copied ? "Code copié" : "Copier le code"}
            </button>
            {copied && (
              <p className="text-xs text-[#0A7F2E] inline-flex items-center gap-1 justify-center">
                <Check size={12} /> Le code est copié !
              </p>
            )}
            {/* QR shortcut to open /display/{id} directly */}
            {displayQrUrl && (
              <div
                className="mt-1 border-2 border-border bg-white p-2 flex flex-col items-center"
                data-testid="tv-display-qr-block"
              >
                <p className="text-[10px] uppercase tracking-wider font-bold text-subtle mb-1 text-center">Scanner pour ouvrir l'écran</p>
                <img
                  src={displayQrUrl}
                  alt="QR code écran public"
                  className="w-28 h-28 sm:w-32 sm:h-32 object-contain"
                  data-testid="tv-display-qr-img"
                />
                <p className="mt-1 text-[10px] text-subtle text-center leading-tight">Ouvre directement<br />votre écran public</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A7F2E]/10 text-[#0A7F2E] text-xs font-bold" data-testid="tv-code-active-badge">
          <ShieldCheck size={14} /> Code actif et permanent pour votre établissement.
        </div>

        {/* Instructions */}
        <div className="mt-5 border border-border bg-[#EFF4FF] px-4 py-4">
          <p className="font-display font-bold text-ink text-base mb-3">Comment l'afficher sur votre TV ?</p>
          <ol className="space-y-3 text-sm text-ink">
            <li className="flex gap-3">
              <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1B5BFF] text-white text-xs font-black">1</span>
              <span>
                <strong>Ouvrez le navigateur internet</strong>
                <br /><span className="text-subtle text-xs">Sur votre télévision (Smart TV, Android TV, Fire TV, etc.).</span>
              </span>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1B5BFF] text-white text-xs font-black">2</span>
              <span>
                <strong>Tapez l'adresse suivante</strong>
                <br />
                <a
                  href={tvHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-1 px-2 py-1 bg-white border border-border font-mono text-[#1B5BFF] font-bold text-sm break-all hover:underline"
                  data-testid="tv-code-tv-url"
                >
                  {tvHost}
                </a>
                <br />
                <span className="text-subtle text-xs">Ouvrez cette adresse dans le navigateur internet de votre télévision.</span>
              </span>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1B5BFF] text-white text-xs font-black">3</span>
              <span>
                <strong>Saisissez le code ci-dessus</strong>
                <br />Entrez le code <strong className="text-[#1B5BFF]">{code || "----"}</strong> puis validez. Votre écran public s'affichera automatiquement.
              </span>
            </li>
          </ol>
        </div>

        <div className="mt-3 inline-flex items-start gap-2 text-xs text-subtle">
          <Lightbulb size={14} className="text-[#F18F18] shrink-0 mt-0.5" />
          <p><strong className="text-ink">Ce code est unique à votre établissement.</strong> Vous pouvez le partager avec votre équipe ou l'utiliser sur autant de télévisions que vous souhaitez.</p>
        </div>
      </div>
    </div>
  );
}
